import ActionPlanDiagnosisSummary from '@/src/components/actions/action-plan-diagnosis-summary/ActionPlanDiagnosisSummary'
import { useSuspenseQuery } from '@tanstack/react-query'
import { t } from 'i18next'
import { useLocation, useParams } from 'react-router-dom'
import { Suspense } from 'react'
import { usePageHeaders } from '@/src/hooks/use-page-headers/usePageHeaders'
import { userPreferencesPort } from '@/infra/config/beans'
import { useUserPreferences } from '@/src/hooks/use-user-preference-port/useUserPreferencePort'
import ActionList from '@/src/components/actions/action-list/ActionList'
import ActionPlanSkeleton from '@/src/components/layout/action-plan/ActionPlanSkeleton'
import ActionCompletionConfirmationModal from '@/src/components/actions/action-completion-confirmation-modal/ActionCompletionConfirmationModal'
import CustomActionsRemainingModal from '@/src/components/actions/custom-actions-remaining-modal/CustomActionsRemainingModal'
import useModalPromise from '@/src/hooks/use-modal-promise/useModalPromise'
import useActionUpdate from '@/src/hooks/use-action-updater/useActionUpdate'
import useActionLabelUpdate from '@/src/hooks/use-action-label-update/useActionLabelUpdate'
import { calculateImprovedScore } from '@/src/utils/scoreUtils'
import BackButton from '@/src/components/global/back-button/BackButton'
import { ActionPlanDetailsDto } from '@/infra/api/generated/model'
import { getDiagnoses } from '@/infra/api/generated/diagnoses/diagnoses'

const { getDiagnosisActionPlan } = getDiagnoses()

export type RequiredActionPlanDetails = ActionPlanDetailsDto & {
  actionPlan: NonNullable<ActionPlanDetailsDto['actionPlan']>
}

const actionPlanQuery = (diagnosisId: string) => ({
  queryKey: ['diagnoses', 'actions', diagnosisId],
  queryFn: () => getDiagnosisActionPlan(diagnosisId),
})

type NavConfig = { to?: string; label: string }

const getNavigationConfig = (source?: string): NavConfig => {
  const configMap: Record<string, NavConfig> = {
    priority: {
      to: undefined, // undefined triggers navigate(-1) in BackButton
      label: t('global.back'),
    },
    myteam: {
      to: undefined,
      label: t('global.back'),
    },
    diagnosis: {
      to: '/my-hub',
      label: t('pages.action-list.back-to-home'),
    },
    actions: {
      to: '/my-hub/actions',
      label: t('global.back'),
    },
  }

  // Fallback to '/my-hub' if source is unknown or undefined
  return configMap[source || ''] || { to: '/my-hub', label: t('pages.action-list.back-to-home') }
}

const ActionsIndex = () => {
  const { team } = useUserPreferences(userPreferencesPort)
  const location = useLocation()

  const navConfig = getNavigationConfig(location.state?.source)

  usePageHeaders(t('pages.action-list.title'), team?.name)

  return (
    <Suspense fallback={<ActionPlanSkeleton />}>
      <ActionsContent navConfig={navConfig} />
    </Suspense>
  )
}

const ActionsContent = ({ navConfig }: { navConfig: NavConfig }) => {
  const { diagnosisId } = useParams<{ diagnosisId: string }>()
  const { data: resolved } = useSuspenseQuery(actionPlanQuery(diagnosisId!))

  if (!resolved.actionPlan) {
    return <PerfectScoreContent diagnosisDetails={resolved} navConfig={navConfig} />
  }
  return (
    <ActionsListContent
      key={resolved.actionPlan.actions?.map((a) => `${a.actionPlanItemId}:${a.label}`).join('|')}
      actionPlanDetails={{ ...resolved, actionPlan: resolved.actionPlan }}
      navConfig={navConfig}
    />
  )
}

type ActionsListContentProps = {
  actionPlanDetails: RequiredActionPlanDetails
  navConfig: NavConfig
}

const ActionsListContent = ({ actionPlanDetails, navConfig }: ActionsListContentProps) => {
  const { family, diagnosis } = actionPlanDetails
  const { isVisible, openModalPromise, handleClose } = useModalPromise()
  const {
    isVisible: isCustomActionsModalVisible,
    openModalPromise: openCustomActionsModalPromise,
    handleClose: handleCustomActionsModalClose,
  } = useModalPromise()
  const {
    actionList,
    handleActionUpdate,
    actionPlanScoreValue,
    isListDisabled,
    actionPlanLastUpdatedAt,
  } = useActionUpdate(actionPlanDetails.actionPlan, openModalPromise, openCustomActionsModalPromise)

  const { handleActionLabelUpdate, handleActionDelete } = useActionLabelUpdate()
  const actionPlanDisplayedScore = calculateImprovedScore(actionPlanScoreValue, diagnosis.score)

  return (
    <>
      <div className="h-full flex flex-col overflow-auto">
        <div className="flex flex-col items-start gap-5 p-5">
          <BackButton onBrand to={navConfig.to} label={navConfig.label} />
          <ActionPlanDiagnosisSummary
            family={family}
            diagnosisDate={diagnosis.endedAt!}
            actionPlanScore={actionPlanDisplayedScore}
            diagnosisScore={diagnosis.score}
            actionPlanLastUpdatedAt={actionPlanLastUpdatedAt!}
          />
        </div>
        <div className="bg-white flex-1 flex flex-col rounded-t-2xl p-5">
          <ActionList
            actions={actionList}
            listIsDisabled={isListDisabled}
            actionValueChange={handleActionUpdate}
            actionLabelChange={handleActionLabelUpdate}
            actionDelete={handleActionDelete}
          />
        </div>
      </div>
      <ActionCompletionConfirmationModal open={isVisible} onClose={handleClose} />
      <CustomActionsRemainingModal
        open={isCustomActionsModalVisible}
        onClose={handleCustomActionsModalClose}
      />
    </>
  )
}

type PerfectScoreContentProps = {
  diagnosisDetails: ActionPlanDetailsDto
  navConfig: NavConfig
}

const PerfectScoreContent = ({ diagnosisDetails, navConfig }: PerfectScoreContentProps) => {
  const { family, diagnosis } = diagnosisDetails

  return (
    <div className="h-full flex flex-col overflow-auto">
      <div className="flex flex-col items-start gap-5 p-5">
        <BackButton onBrand to={navConfig.to} label={navConfig.label} />

        <ActionPlanDiagnosisSummary
          family={family}
          diagnosisDate={diagnosis.endedAt!}
          actionPlanScore={diagnosis.score}
          diagnosisScore={diagnosis.score}
          actionPlanLastUpdatedAt={diagnosis.endedAt!}
        />
      </div>

      <div className="bg-white flex-1 flex flex-col rounded-t-3xl px-5 py-8">
        <div className="w-full mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2 leading-normal">
            {t('pages.action-list.perfect-score.title')}
          </h2>

          <p className="text-gray-700 text-base font-medium leading-normal">
            {t('pages.action-list.perfect-score.description')}
          </p>
        </div>

        <div className="flex flex-1 justify-center items-center w-full">
          <img
            src="/icons/yoga-woman.svg"
            alt="Aucune action requise"
            className="w-full max-w-87.5 aspect-square object-contain"
          />
        </div>
      </div>
    </div>
  )
}

export default ActionsIndex
